/**
 * Dynasty upgrades (docs/upgrades.md): the limits on a proposal and the pledge arithmetic, shared
 * by the server (server/db.ts), the screens (src/views/Upgrade*.tsx) and the Node tests. Pure and
 * dependency-free like shared/rewards.ts.
 */

export const UPGRADE_TITLE_MAX = 60;
export const UPGRADE_NOTES_MAX = 500;
export const UPGRADE_GOAL_MIN = 1;
export const UPGRADE_GOAL_MAX = 1_000_000;

/** The quick amounts on the pledge control, before "All". */
export const PLEDGE_CHIPS: readonly number[] = [10, 50, 100, 500];

/** Why a proposal cannot be made, or null when it can. */
export function upgradeInputErrors(input: { title: unknown; notes?: unknown; goal: unknown; photo_id?: unknown }): string | null {
  const title = typeof input.title === "string" ? input.title.trim() : "";
  if (!title) return "Give it a name";
  if (title.length > UPGRADE_TITLE_MAX) return `Keep the name to ${UPGRADE_TITLE_MAX} characters`;
  if (input.notes !== undefined && input.notes !== null && typeof input.notes !== "string") return "The note must be text";
  const notes = typeof input.notes === "string" ? input.notes.trim() : "";
  if (notes.length > UPGRADE_NOTES_MAX) return `Keep the note to ${UPGRADE_NOTES_MAX} characters`;
  const goal = input.goal;
  if (typeof goal !== "number" || !Number.isInteger(goal)) return "The goal must be a whole number of wood";
  if (goal < UPGRADE_GOAL_MIN || goal > UPGRADE_GOAL_MAX) return `The goal must be ${UPGRADE_GOAL_MIN} to ${UPGRADE_GOAL_MAX.toLocaleString("en-US")} wood`;
  if (input.photo_id !== undefined && (typeof input.photo_id !== "string" || !input.photo_id.trim())) return "An upgrade needs a photo";
  return null;
}

/** Wood still needed: never negative. */
export function remainingFor(goal: number, pledged: number): number {
  return Math.max(goal - pledged, 0);
}

/** How full the bar is, 0..1, capped at 1. */
export function progressRatio(goal: number, pledged: number): number {
  if (goal <= 0) return 1;
  return Math.min(Math.max(pledged / goal, 0), 1);
}

export type UpgradeState = "open" | "funded" | "built";

/** Open until the goal is met, funded until someone builds it, then built. */
export function upgradeState(u: { goal: number; pledged: number; built_at: string | null }): UpgradeState {
  if (u.built_at) return "built";
  return u.pledged >= u.goal ? "funded" : "open";
}

export type PledgeProblem = "invalid" | "funded" | "short";

export type PledgeCheck = { ok: true; amount: number } | { ok: false; reason: PledgeProblem; message: string };

const PLEDGE_MESSAGES: Record<PledgeProblem, string> = {
  invalid: "Put in a whole number of wood",
  funded: "Already funded",
  short: "Not enough wood",
};

/**
 * The wood a pledge actually puts in: `requested`, clamped to what the upgrade still needs so a
 * pledge never overfunds it. Refused when the request is not a positive whole number, when nothing
 * is needed any more, or when the clamped amount is more than the peon holds.
 */
export function pledgeAmountFor(requested: number, remaining: number, balance: number): PledgeCheck {
  if (typeof requested !== "number" || !Number.isInteger(requested) || requested <= 0) return { ok: false, reason: "invalid", message: PLEDGE_MESSAGES.invalid };
  if (remaining <= 0) return { ok: false, reason: "funded", message: PLEDGE_MESSAGES.funded };
  const amount = Math.min(requested, remaining);
  if (amount > balance) return { ok: false, reason: "short", message: PLEDGE_MESSAGES.short };
  return { ok: true, amount };
}

// ---------- Household wallpaper (docs/upgrades.md "Wallpaper", flag `wallpaperUI`) ----------

/** The wallpapers, in the order they are built. A wallpaper upgrade is an upgrades row of kind 'wallpaper'. */
export type WallpaperKey = "stone" | "marble" | "gold";

export interface Wallpaper {
  key: WallpaperKey;
  /** As the card reads it: "Stone", "Marble", "Gold leaf". */
  name: string;
  /** Wood to build it. */
  goal: number;
  /** The wallpaper that must be built first, or null for the first. */
  needs: WallpaperKey | null;
}

/** Tunables: the order, the names, the goals and what each needs built first. */
export const WALLPAPERS: readonly Wallpaper[] = [
  { key: "stone", name: "Stone", goal: 500, needs: null },
  { key: "marble", name: "Marble", goal: 1000, needs: "stone" },
  { key: "gold", name: "Gold leaf", goal: 2000, needs: "marble" },
];

/** The household_settings key holding the household's current wallpaper. */
export const WALLPAPER_SETTING_KEY = "wallpaper";

/** The wallpaper with this key, or null for anything else (an unknown or absent value is no wallpaper). */
export function wallpaperOf(key: unknown): Wallpaper | null {
  return WALLPAPERS.find((w) => w.key === key) ?? null;
}

/** Its place in the order: 0 for Stone. -1 for anything that is not a wallpaper. */
export function wallpaperRank(key: unknown): number {
  return WALLPAPERS.findIndex((w) => w.key === key);
}

/** Whether `key` may be built given the wallpapers already built: what it needs is among them. */
export function wallpaperUnlocked(key: WallpaperKey, built: readonly string[]): boolean {
  const w = wallpaperOf(key);
  return !!w && (w.needs === null || built.includes(w.needs));
}

/** The household's current wallpaper: the highest built one, or null when none is built. */
export function currentWallpaper(built: readonly string[]): WallpaperKey | null {
  let best = -1;
  for (const k of built) best = Math.max(best, wallpaperRank(k));
  return best >= 0 ? WALLPAPERS[best].key : null;
}

/** The next wallpaper to pot for: the first in order not built whose needs are built; null once all are. */
export function nextWallpaper(built: readonly string[]): Wallpaper | null {
  return WALLPAPERS.find((w) => !built.includes(w.key) && wallpaperUnlocked(w.key, built)) ?? null;
}

/** The texture the shell draws: the wallpaper while the flag is on and one is built, else the board's own. */
export function shownWallpaper(flagOn: boolean, wallpaper: unknown): WallpaperKey | null {
  return flagOn ? (wallpaperOf(wallpaper)?.key ?? null) : null;
}

/**
 * The Wallpaper card's lines (both clients): `built` names the current one and what comes next
 * ("Stone, built. Next: Marble", or "Gold leaf, built." at the end), null before anything is built;
 * `progress` is the bar's line for the one being potted ("Stone · 320 of 500 wood"), null once all are built.
 */
export function wallpaperCardLines(current: string | null, next: { wallpaper: string; pledged: number; goal: number } | null): { built: string | null; progress: string | null } {
  const now = wallpaperOf(current);
  const upcoming = next ? wallpaperOf(next.wallpaper) : null;
  const built = now ? (upcoming ? `${now.name}, built. Next: ${upcoming.name}` : `${now.name}, built.`) : null;
  const progress = next && upcoming ? `${upcoming.name} · ${next.pledged.toLocaleString("en-US")} of ${next.goal.toLocaleString("en-US")} wood` : null;
  return { built, progress };
}

/** A wallpaper upgrade's title, as its screen and the push name it: "Stone wallpaper". */
export function wallpaperTitle(w: Wallpaper): string {
  return `${w.name} wallpaper`;
}
